import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Layout,
    List,
    Typography,
    Divider,
    Menu,
    Modal,
    Popover, Space
} from 'antd';
import {LeaderboardRounded, ArrowUpwardOutlined} from "@mui/icons-material";
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import {selectFullPortfolios} from "../../../Store/slices/portfolios.slice";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import {
    FolderOpenRounded, EditNoteRounded, NoteAddRounded,
    AttachEmailRounded, FolderSpecialRounded, LightbulbRounded
} from "@mui/icons-material";
import CopyText from "../../../Components/Text/CopyText";
import {deletePages, updateUser} from "../../../Api/userApi";
import PageDefaultsForm from "./PageDefaultsForm";
import TableSearch from "../../../Components/TableSearch";
import PageHeader from "../../../Components/PageHeader";
import {iconMapping} from "../../../Configs/AppsConfig";
import { ExclamationCircleOutlined, DownOutlined } from '@ant-design/icons';
import {RiMenuUnfold3Fill} from "@remixicon/react";
import {DeleteForeverRounded} from "@mui/icons-material";


const BANNER_ARTICLE_KEY = 'showpagecollectionbanner1';


const PageMenu = ({handleRemove})=>{
    return (
        <Popover
            content={
            <Menu>
                <Menu.Item key="1" icon={<DeleteForeverRounded/>} onClick={handleRemove}>
                    Remove
                </Menu.Item>
            </Menu>
            }
        >
            <RiMenuUnfold3Fill size={16}/>
        </Popover>
    )
}

const PageCollection = ({ pageData, onEdit }) => {
  const [showBanner, setShowBanner] = useState(localStorage.getItem(BANNER_ARTICLE_KEY) || 1);
  const dispatch = useDispatch();
  const fullPortfolios = useSelector((state) => {
      const portfolios = selectFullPortfolios(state, [...new Set(pageData.flatMap(obj => obj.portfolio_ids).filter(String))]);
      return portfolios.reduce((acc, curr) => {
          acc[curr.id] = curr;  // Assume each portfolio has an 'id' and 'name' field
          return acc;
          }, {});
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  // Function to handle selection changes
  const onSelectChange = selectedKeys => {
    setSelectedRowKeys(selectedKeys);
  };


  // Function to confirm deletion
  const confirmDeletion = () => {
    Modal.confirm({
      title: 'Are you sure you want to delete the selected pages?',
      icon: <ExclamationCircleOutlined />,
      content: 'This action cannot be undone.',
      onOk() {
        dispatch(deletePages({ pageIds: selectedRowKeys }));
        setSelectedRowKeys([]);
      },
    });
  };


    const menu = <PageMenu handleRemove={confirmDeletion} />;
    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
        columnTitle:menu,
    };


  const columns = [
    {
      title: 'Name',
      dataIndex: 'title',
      key: 'title',
      render: (title, record)=>(
          <div className={'cursor'} onClick={()=>onEdit(pageData.find(obj=>obj.page_id===record.page_id))}>
            <Typography.Text strong>
              {title} <EditNoteRounded fontSize={'small'} />
            </Typography.Text>
          </div>
      ),
       searchable: true,
    },
    {
      title: "Link",
      dataIndex: 'id',
      key: 'id',
      render: (id, record)=><CopyText text={`${window.location.origin}/public/pages/${record.page_id}`} />,
    },
    {
      title: 'Visibility',
      dataIndex: 'is_public',
      key: 'is_public',
      render: (value) => (
          <Typography.Text style={{color: value === true ? 'red' : '', fontWeight: value === true ? '600': '400'}}>{value===true?'Public':'Private'}</Typography.Text>
      ),
    },
    {
      title: 'Portfolios',
      dataIndex: 'portfolio_ids',
      key: 'portfolio_ids',
      sorter: (a, b) => a.portfolio_ids.length - b.portfolio_ids.length,
      render: (portfolioIds) => (
        <Popover
          content={
            <List
              size="small"
              dataSource={portfolioIds.map(id => fullPortfolios[id]?.name || id)}
              renderItem={(name) => <List.Item>{name}</List.Item>}
            />
          }
          title="Portfolios"
          trigger="click"
        >
            <Button
              className={'px-3'}
              color={portfolioIds.length > 0 ? 'primary' : 'inherit'}
              variant={'text'}
              startIcon={<FolderOpenRounded fontSize={'small'}/>}

          >
                {portfolioIds.length}
          </Button>
        </Popover>
      ),
    },
    {
      title: 'Files',
      dataIndex: 'files',
      key: 'files',
      render: (files) => (
        <Popover
          content={
            <List
              size="small"
              dataSource={files}
              renderItem={(name) => <List.Item>{name}</List.Item>}
            />
          }
          title="Files"
          trigger="click"
        >
          <Button
              className={'px-3'}
              color={files.length > 0 ? 'primary' : 'inherit'}
              variant={'text'}
              startIcon={<NoteAddRounded fontSize={'small'}/>}
              label={files.length}

          >
                {files.length}
          </Button>
        </Popover>
      ),
    },
    {
      title: 'Emails',
      dataIndex: 'emails',
      key: 'emails',
      render: (emails) => (
        <Popover
          content={
            <List
              size="small"
              dataSource={emails}
              renderItem={(item) => <List.Item>{item}</List.Item>}
            />
          }
          title="Emails"
          trigger="click"
        >
            <Button
              className={'px-3'}
              color={emails.length > 0 ? 'primary' : 'inherit'}
              variant={'text'}
              startIcon={<AttachEmailRounded fontSize={'small'}/>}
          >
                {emails.length}
          </Button>
        </Popover>
      ),
    },
  ];

  return (
      <>
          <Layout>
              <Layout.Sider width={300} className={'full left white'} style={{padding: '30px 20px'}}>
                  <PageDefaultsForm />
                  <List>
                      {Object.entries(fullPortfolios).map(([id, portfolio]) => (
                            <List.Item key={id}>
                                {portfolio.name}
                            </List.Item>

                      ))}
                  </List>
              </Layout.Sider>
              <Layout.Content style={{padding: '0 30px'}}>
                  {showBanner === 1 && (
                      <>
                        <Alert className={'m-3'} onClose={()=>{localStorage.setItem(BANNER_ARTICLE_KEY, 0);setShowBanner(0)}}>
                              <AlertTitle>Introducing <b>Pages&trade;</b> </AlertTitle>
                              IQ is our new way for you to engage others with personalized content including portfolios, files, and other
                              resources you might want to include
                          </Alert>
                          <Divider/>
                      </>
                  )}
                  <Space className={'p-3'}>
                      <Chip icon={<LightbulbRounded fontSize={'small'}/>} label={`${pageData.length} IQ Pages`} color={'primary'} />
                      <Chip sx={{paddingLeft: 1, paddingRight:1}}
                            icon={<LeaderboardRounded fontSize={'small'}/>}
                            label={'130 Total Views'}
                            onDelete={()=>{}}
                            deleteIcon={<ArrowUpwardOutlined sx={{color: 'green'}}/>}
                            color={'primary'}
                      />
                      <Chip sx={{paddingLeft: 1, paddingRight:1}} icon={<FolderSpecialRounded fontSize={'small'}/>} label={`${Object.keys(fullPortfolios).length} Portfolios`} color={'primary'} />
                  </Space>
                  <TableSearch
                    dataSource={pageData}
                    columns={columns}
                    rowKey="page_id"
                    rowSelection={rowSelection}
                />
              </Layout.Content>
          </Layout>
      </>
  );
};

export default PageCollection;